import { Button, Icon, Loading, Modal, SearchInput, Tooltip, Typography } from "@/components/atoms";
import { PricingProfile } from "@/redux/slices/trip/types";
import { useEffect, useState } from "react";
import { renderCompanyAvatar } from "./helpers";
import { useLazySearchCompaniesQuery, useUpdateCompanyMutation } from "@/redux/apis/company/companyApi";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast, clsx } from "@/utils";
import { Link } from "react-router-dom";
import { UpdateCompanyParams } from "@/redux/apis/company/types";
import { Company } from "@/redux/slices/company/types";

interface SearchAndAttachCompanyModalProps {
  open: boolean;
  onClose: () => void;
  profile: PricingProfile;
}

export const SearchAndAttachCompanyModal = ({ open, onClose, profile }: SearchAndAttachCompanyModalProps) => {
  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();
  const [searchCompany, { isFetching, data }] = useLazySearchCompaniesQuery();
  const [search, setSearch] = useState<string>();
  const companies = data?.data.companies;

  useEffect(() => {
    if (search) searchCompany({ search }).unwrap().catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const handleAttachCompany = (company: Company) => {
    // TODO: Figure out why, in the backend, the entire company object gets updated when I just update a couple fields.
    if (company) {
      const { invoice, pricing, notes } = company;
      const companyData: UpdateCompanyParams = {
        uuid: company.id,
        name: company.name,
        address: company.address,
        phone: company.phone,
        business_registration_type: company.businessRegistrationType,
        business_registration_id: company.businessRegistrationId,
        notes: {
          reference: notes.reference,
          admin: notes.admin,
          driver: notes.driverNotes,
        },
        key_contact: company.keyContact,
        invoice: {
          enabled: invoice.enabled,
          generate_on_level: invoice.generateOnLevel ? invoice.generateOnLevel : undefined,
          generate_on_frequency: invoice.generateOnFrequency ? invoice.generateOnFrequency : undefined,
          generate_on_date: invoice.generateOnDate ? invoice.generateOnDate : undefined,
          payment_terms: invoice.paymentTerms ? invoice.paymentTerms : undefined,
        },
        billing_contacts: company.billingContacts,
        pricing: {
          adjustments: pricing.adjustments,
          commission: {
            override_enabled: pricing.commission.overrideEnabled,
            percentage: pricing.commission.percentage ? pricing.commission.percentage : undefined,
          },
          profile_uuid: profile.id,
          allow_driver_extras: pricing.allowDriverExtras,
          include_client_extras_in_base_cost: true,
        },
      };

      updateCompany(companyData)
        .unwrap()
        .then(() => {
          addToast("success", "Successfully Attached Company");
          handleClose();
        })
        .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    } else handleClose();
  };

  return (
    <Modal
      className="w-[85vw] md:w-[550px] lg:w-[550px]"
      open={open}
      onClose={handleClose}
    >
      <div className="relative flex flex-col">
        {isLoading && <Loading />}
        <Typography variant="title">Search and Attach Company</Typography>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">Search for a company to link with this pricing profile and start using it.</Typography>
        <SearchInput 
          className="mt-5" 
          search={search} 
          onSearch={(value) => setSearch(value)} 
          placeholder="Search Company" 
          isLoading={isFetching}
        />
        <div className="flex flex-col overflow-y-auto mt-4 gap-y-2 h-[357px]">
          {companies?.map(company => {
            const profileId = company.pricing.profileId;
            const isAlreadyAdded = profileId === profile.id;
            return (
              <div key={company.id} className="flex flex-row items-center justify-between rounded-lg bg-neutral-surface-gray p-2 gap-2">
                <div className="flex flex-row items-center gap-x-3">
                  {renderCompanyAvatar(company)}
                  <Link to={`/companies/${company.id}`} target="_blank">
                    <Typography 
                      variant="paragraph" 
                      className={clsx(
                        "text-neutral-dark-gray cursor-pointer hover:text-info hover:underline", 
                        { "text-neutral-silver-gray": profileId },
                      )}
                    >
                      {company.name}
                    </Typography>
                  </Link>
                </div>
                {isAlreadyAdded ? (
                  <div className="flex flex-row items-center gap-x-2">
                    <Icon name="TickCircle" variant="Bold" size="sm" className="text-success" />
                    <Typography variant="paragraph" className="text-neutral-silver-gray">Already Added</Typography>
                  </div>
                ) : profileId ? (
                  <Tooltip content="Company is attached to a different pricing profile" placement="top">
                    <Icon name="Danger" variant="Bold" size="sm" className="text-warning" />
                  </Tooltip>
                ) : <Button variant="secondary" startIcon="Add" size="sm" onClick={() => handleAttachCompany(company)}>Attach Company</Button>}
              </div>
            );
          })}
        </div>
        <div className="flex flex-row items-center justify-end w-full gap-3 mt-8">
          <Button variant="secondary" size="lg" onClick={handleClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
